import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Popper, Tooltip } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/BookmarkBorderRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { deleteClipFilter, getSavedClipFilters, saveClipFilter } from '../../api/clipFiltersApi';
import Modal from '../modules/Modal';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import { useClipFilter } from './context';
import { SavedClipFilter, tokenKey, tokenLabel } from './types';
import styles from './Filter.module.scss';

const sortPresets = (presets: SavedClipFilter[]) => presets.slice().sort((a, b) => a.name.localeCompare(b.name) || Number(a.id) - Number(b.id));

export const SavedFilters = () => {
  const { config, setConfig, active, sidebarVisible } = useClipFilter();
  const [presets, setPresets] = useState<SavedClipFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState('');
  const [revision, setRevision] = useState(0);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const [preview, setPreview] = useState<{ preset: SavedClipFilter; anchorEl: HTMLElement } | null>(null);
  const previewCloseTimer = useRef<number>();
  const keepPreviewOpen = () => window.clearTimeout(previewCloseTimer.current);
  const closePreview = () => {
    keepPreviewOpen();
    previewCloseTimer.current = window.setTimeout(() => setPreview(null), 150);
  };

  useEffect(() => () => window.clearTimeout(previewCloseTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    getSavedClipFilters().then(({ data }) => {
      if (!cancelled) setPresets(sortPresets(data));
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [revision]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!active || !name.trim() || pendingRef.current || loading || loadError) return;
    pendingRef.current = true;
    setPending('save');
    setActionError('');
    try {
      const { data } = await saveClipFilter(name.trim(), config);
      setPresets((previous) => sortPresets([...previous, data]));
      setShowSave(false);
      setName('');
    } catch {
      setActionError('Could not save this filter. Please try saving again.');
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  };

  const remove = async (preset: SavedClipFilter) => {
    if (pendingRef.current) return;
    setPreview(null);
    pendingRef.current = true;
    setPending(String(preset.id));
    setActionError('');
    try {
      await deleteClipFilter(preset.id);
      setPresets((previous) => previous.filter((item) => String(item.id) !== String(preset.id)));
    } catch {
      setActionError('Could not delete this saved filter. Please try deleting again.');
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  };

  const apply = (preset: SavedClipFilter) => setConfig({
    ...preset.config, tokens: preset.config.tokens.map((token) => ({ ...token })),
  });

  return <section className={styles.savedFilters} aria-label="Saved filters">
    <div className={styles.heading}>
      <h3>Saved filters <span className={styles.personal}>(yours)</span></h3>
      <button type="button" className={styles.saveCurrent} disabled={!active || loading || loadError || pending !== null}
        onClick={() => { setShowSave(true); setActionError(''); }}><BookmarkIcon /> Save current</button>
    </div>
    {showSave && <Modal open onClose={pending === 'save' ? undefined : () => setShowSave(false)} loading={pending === 'save'}>
      <form className={styles.saveModal} onSubmit={save} onKeyDown={(event) => event.stopPropagation()}>
        <div className={styles.saveModalHeader}>
          <h2>Save current filter</h2>
          <button type="button" className={styles.iconButton} aria-label="Close save filter" disabled={pending === 'save'} onClick={() => setShowSave(false)}><CloseIcon /></button>
        </div>
        <label>
          Filter name
          <input className={styles.input} autoFocus aria-label="Saved filter name" placeholder="Filter name" value={name} maxLength={255}
            disabled={pending === 'save'} onChange={(event) => setName(event.target.value)} />
        </label>
        <h3>Rules to save ({config.tokens.length})</h3>
        <p className={styles.hint}>All rules below will be saved together as one filter.</p>
        <ul className={styles.saveRules}>
          {config.tokens.map((token) => <li key={tokenKey(token)} className={styles.token}><span>{tokenLabel(token)}</span></li>)}
        </ul>
        <p className={styles.hint}>Match {config.match === 'all' ? 'All' : 'Any'} · Filter {config.mode === 'in' ? 'In' : 'Out'}</p>
        {actionError && <p className={styles.status} role="alert">{actionError}</p>}
        <div className={styles.saveModalActions}>
          <OutlinedButton disabled={pending === 'save'} onClick={() => setShowSave(false)}>Cancel</OutlinedButton>
          <ContainedButton type="submit" disabled={!name.trim() || !active || pending !== null || loading || loadError}>Save</ContainedButton>
        </div>
      </form>
    </Modal>}
    {actionError && !showSave && <p className={styles.status} role="alert">{actionError}</p>}
    {loading ? <p className={styles.status} role="status"><CircularProgress size={14} /> Loading saved filters…</p>
      : loadError ? <div className={styles.status} role="alert">Could not load saved filters.
        <button type="button" onClick={() => setRevision((value) => value + 1)}>Retry</button>
      </div>
        : presets.length ? <ul className={styles.savedList}>
          {presets.map((preset) => <li key={preset.id} className={styles.savedItem}
            onMouseEnter={(event) => { keepPreviewOpen(); setPreview({ preset, anchorEl: event.currentTarget }); }}
            onMouseLeave={closePreview}>
            <button type="button" className={styles.applyPreset} aria-label={`Apply saved filter ${preset.name}`} onClick={() => apply(preset)}>
              <span><strong>{preset.name}</strong><small>{preset.config.tokens.length} {preset.config.tokens.length === 1 ? 'rule' : 'rules'} · Filter {preset.config.mode === 'in' ? 'In' : 'Out'} · Match {preset.config.match === 'all' ? 'All' : 'Any'}</small></span>
              <span className={styles.applyLabel}>Apply</span>
            </button>
            <Tooltip title={`Delete saved filter “${preset.name}”`}><span>
              <button type="button" className={styles.iconButton} aria-label={`Delete saved filter ${preset.name}`} disabled={pending !== null}
                onClick={() => remove(preset)}>{pending === String(preset.id) ? <CircularProgress size={14} /> : <DeleteIcon />}</button>
            </span></Tooltip>
          </li>)}
        </ul> : <p className={styles.hint}>No saved filters yet.</p>}
    <Popper open={!!preview && sidebarVisible && !showSave} anchorEl={preview?.anchorEl} placement="left-start"
      popperOptions={{ strategy: 'fixed' }} sx={{ zIndex: 1300 }}
      container={() => document.fullscreenElement || document.body}
      modifiers={[{ name: 'offset', options: { offset: [0, 10] } },
        { name: 'flip', options: { fallbackPlacements: ['bottom-end'] } },
        { name: 'preventOverflow', options: { padding: 8 } }]}>
      {preview && <div className={styles.dropdown} data-clip-filter-panel onMouseEnter={keepPreviewOpen} onMouseLeave={closePreview}>
        <h3 className={styles.group}>{preview.preset.name}</h3>
        <ul className={`${styles.results} ${styles.previewRules}`}>
          {preview.preset.config.tokens.map((token) => <li key={tokenKey(token)} className={styles.result}><span>{tokenLabel(token)}</span></li>)}
        </ul>
        <div className={styles.previewFooter}>
          <p className={styles.keyboardHint}>{preview.preset.config.tokens.length} {preview.preset.config.tokens.length === 1 ? 'rule' : 'rules'} · Match {preview.preset.config.match === 'all' ? 'All' : 'Any'} · Filter {preview.preset.config.mode === 'in' ? 'In' : 'Out'}</p>
          <ContainedButton ariaLabel={`Apply saved filter ${preview.preset.name}`} onClick={() => { apply(preview.preset); setPreview(null); }}>Apply</ContainedButton>
        </div>
      </div>}
    </Popper>
  </section>;
};
